<template>
  <!-- Main Content -->
  <main class="py-4 sm:py-8 sm:p-8">
    <!-- Header -->
    <div class="mb-10 flex items-center justify-between">
      <div>
        <PageTitle>
          Gerencie seus links de redirecionamento
        </PageTitle>
        <PageDescription>
          Administre e personalize seus links de redirecionamento com facilidade.
        </PageDescription>
      </div>
      <RegisterButton @click="openNewLinkDialog">Cadastrar link</RegisterButton>
    </div>

    <!-- buttons Table Card -->
    <TableContainer>
      <!-- Table Header -->
      <div class="mb-6 flex items-center justify-between">
        <h2 class="text-xl font-semibold text-white">Links registrados</h2>
        <div class="flex items-center gap-4"></div>
      </div>

      <!-- Table -->
      <Table class="hidden md:block">
        <TableHeader>
          <TableRow>
            <TableHead
              v-for="header in tableHeaders"
              :key="header.key"
              :class="header.class"
            >
              <div class="flex items-center gap-2">
                {{ header.label }}
                <ArrowsUpDownIcon 
                  v-if="header.sortable"
                  class="h-4 w-4 cursor-pointer text-gray-500 hover:text-white"
                  @click="sortBy(header.key)"
                />
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <template v-if="buttons">
            <TableFallback v-if="buttons.data.length === 0" :colspan="tableHeaders.length" :icon="LinkIcon" title="Nenhum botão encontrado" :description="`Clique em 'Cadastrar botão' para começar`" />
            <TableRow
              v-for="button in buttons.data"
              :key="button.id"
            >
              <TableCell>
                <div 
                  class="p-2 px-8 rounded inline-flex py-2.5 px-3 text-center relative"
                  :style="{ 
                    backgroundColor: button.style === 'primary' ? '#2B6B84' : 
                                    button.style === 'success' ? '#377531' : 
                                    button.style === 'danger' ? '#A44236' : '#2c3b4b4f' 
                  }"
                >
                  <div class="text-sm font-medium text-white">{{ button.name }}</div>
                  <ArrowUpRight class="size-3 absolute right-1 top-1 text-white" />
                </div>
              </TableCell>
              <TableCell>
                <div class="flex items-center gap-2">
                  <div class="max-w-md truncate text-sm text-blue-300">
                    {{ button.url }}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="opacity-0 group-hover:opacity-100"
                    @click="copyLink(button.url)"
                  >
                    <ClipboardIcon class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="opacity-0 group-hover:opacity-100"
                    @click="openLink(button.url)"
                  >
                    <ExternalLinkIcon class="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge :status="button.status" />
              </TableCell>
              <TableCell>
                <div class="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    @click="editButton(button)"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="hover:text-red-400"
                    @click="deleteLink(button)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </template>
          <TableRowSkeleton v-else :columns="tableHeaders.length" />
        </TableBody>
      </Table>

      <!-- Mobile List -->
      <div class="space-y-4 md:hidden text-left" v-if="buttons">
        <template v-if="buttons.data.length > 0">
          <MobileCard v-for="button in buttons.data" :key="button.id">
            <template #header>
               <div class="flex items-center gap-2">
                 <div class="flex flex-col">
                  <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider">Link</span>
                  <div class="flex items-center gap-2">
                    <div 
                      class="p-1 px-3 rounded inline-flex text-center relative"
                      :style="{ 
                        backgroundColor: button.style === 'primary' ? '#2B6B84' : 
                                        button.style === 'success' ? '#377531' : 
                                        button.style === 'danger' ? '#A44236' : '#2c3b4b4f' 
                      }"
                    >
                        <span class="text-white font-bold text-lg">{{ button.name }}</span>
                    </div>
                  </div>
                 </div>
               </div>
            </template>
            
            <template #actions>
               <div class="flex flex-col items-end gap-2">
                  <div class="flex items-center gap-1">
                    <button @click="editButton(button)" class="text-zinc-400 hover:text-white p-1">
                      <PencilIcon class="size-5" />
                    </button>
                    <button @click="deleteLink(button)" class="text-zinc-400 hover:text-red-400 p-1">
                      <TrashIcon class="size-5" />
                    </button>
                  </div>
               </div>
            </template>
            
            <template #content>
               <MobileDataLabel label="URL">
                  <div class="flex items-center gap-2 bg-white/5 p-2 rounded justify-between">
                     <div class="font-mono text-xs text-blue-300 truncate max-w-[250px]">
                       {{ button.url }}
                     </div>
                     <div class="flex items-center">
                        <button
                          @click="copyLink(button.url)"
                          class="rounded-lg p-1.5 text-zinc-400 transition-all hover:bg-white/10 hover:text-white"
                        >
                          <ClipboardIcon class="size-4 text-white" />
                        </button>
                        <button
                          @click="openLink(button.url)"
                          class="rounded-lg p-1.5 text-zinc-400 transition-all hover:bg-white/10 hover:text-white"
                        >
                           <ArrowUpRight class="size-4 text-white" />
                        </button>
                     </div>
                  </div>
               </MobileDataLabel>

               <div class="flex items-center justify-between pt-2">
                  <div class="flex flex-col">
                    <span class="text-[10px] uppercase font-bold text-zinc-500 tracking-wider mb-1">Status</span>
                    <StatusBadge :status="button.status" />
                  </div>
               </div>
            </template>
          </MobileCard>
        </template>
        <div v-else class="text-center py-12 text-zinc-500">
           <LinkIcon class="size-8 mx-auto mb-3 text-zinc-700" />
           Nenhum botão encontrado
        </div>
      </div>

      <!-- Pagination -->
      <Pagination @update:page="page => reloadAndGetAll({ page, perPage })" v-if="buttons?.pagination" :data="buttons?.pagination" v-model:per-page="perPage" />
    </TableContainer>
  </main>

  <DeleteConfirmationDialog v-model:open="isDeleteButtonDialogOpen" :loading="isDeletingButton" @confirm="isDeletingButton = true; destroy(deletingButton!).then(() => {
    toast({
      title: 'Link excluído com sucesso',
      class: 'bg-green-500 text-white',
      duration: 1500
    })
    isDeletingButton = false
    isDeleteButtonDialogOpen = false
    const index = buttons?.data.findIndex(button => button.id == deletingButton)
    if (index !== -1) buttons?.data.splice(index!, 1)
  })" />

  <!-- New/Edit Link Dialog -->
  <Dialog v-model:open="isButtonDialogOpen" @close="closeButtonDialog">
    <DialogContent class="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>{{ editingButton ? 'Editar link' : 'Novo link' }}</DialogTitle>
        <DialogDescription>
          Configure as informações do link de redirecionamento.
        </DialogDescription>
      </DialogHeader>
      <div class="grid gap-6 py-4">
        <div class="grid gap-2">
          <Label>Nome do botão <span class="text-red-500">*</span></Label>
          <Input v-model="buttonForm.name" placeholder="Ex: Nosso site" />
        </div>
        <div class="grid gap-2">
          <Label>URL de redirecionamento <span class="text-red-500">*</span></Label>
          <Input v-model="buttonForm.url" placeholder="https://exemplo.com" />
        </div>
        <div class="grid gap-2">
          <Label>Cor do botão</Label>
          <div class="flex items-center gap-3">
            <button
              type="button"
              @click="buttonForm.style = undefined"
              class="size-8 rounded-full border-2 transition-all hover:scale-110"
              :class="[buttonForm.style === undefined || buttonForm.style === '' ? 'border-white scale-110' : 'border-transparent']"
              style="background-color: #2c3b4b4f"
              title="Padrão (Cinza)"
            ></button>
            <button
              type="button"
              @click="buttonForm.style = 'primary'"
              class="size-8 rounded-full border-2 transition-all hover:scale-110"
              :class="[buttonForm.style === 'primary' ? 'border-white scale-110' : 'border-transparent']"
              style="background-color: #2B6B84"
              title="Azul"
            ></button>
            <button
              type="button"
              @click="buttonForm.style = 'success'"
              class="size-8 rounded-full border-2 transition-all hover:scale-110"
              :class="[buttonForm.style === 'success' ? 'border-white scale-110' : 'border-transparent']"
              style="background-color: #377531"
              title="Verde"
            ></button>
            <button
              type="button"
              @click="buttonForm.style = 'danger'"
              class="size-8 rounded-full border-2 transition-all hover:scale-110"
              :class="[buttonForm.style === 'danger' ? 'border-white scale-110' : 'border-transparent']"
              style="background-color: #A44236"
              title="Vermelho"
            ></button>
          </div>
        </div>
        <div class="grid gap-2 col-span-full">
          <div class="flex items-center gap-2">
            <Label>Status <span class="text-red-500">*</span></Label>
            <TooltipProvider>
              <Tooltip :delay-duration="100">
                <TooltipTrigger as-child>
                  <CircleHelp class="size-5 text-primary" />
                </TooltipTrigger>
                <TooltipContent side="top" :side-offset="5">
                  Status oculto: Este botão não será exibido <br> quando o usuário iniciar o bot.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select v-model="buttonForm.status">
            <SelectTrigger>
              <SelectValue placeholder="Selecione o status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="status in ButtonStatusEnum" :value="status">{{ ButtonStatusLabels[status] }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div v-if="buttonForm.status === ButtonStatusEnum.ACTIVE" class="grid gap-2 col-span-full">
          <div class="flex items-center gap-2">
            <Label>Posição <span class="text-red-500">*</span></Label>
          </div>
          <Select v-model="buttonForm.placement">
            <SelectTrigger>
              <SelectValue placeholder="Selecione a posição" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="status in ButtonPlacementEnum" :value="status">{{ ButtonPlacementLabels[status] }}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter>
        <Button class="text-white" variant="ghost" @click="closeButtonDialog">Cancelar</Button>
        <Button :disabled="isSaving || (!buttonForm.name || !buttonForm.url)" @click="saveButton">
          <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
          {{ editingButton ? 'Salvar alterações' : 'Cadastrar link' }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
  
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import {
  ArrowsUpDownIcon,
  LinkIcon,
  ClipboardIcon,
  // ExternalLinkIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/vue/24/outline'
import MobileCard from '@/components/ui/mobile-card/MobileCard.vue'
import MobileDataLabel from '@/components/ui/mobile-card/MobileDataLabel.vue'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ArrowUpRight, CircleHelp } from 'lucide-vue-next'
import RegisterButton from '@/components/ui/register-button/RegisterButton.vue'
import Table from '@/components/ui/table/Table.vue'
import TableHeader from '@/components/ui/table/TableHeader.vue'
import TableBody from '@/components/ui/table/TableBody.vue'
import TableRow from '@/components/ui/table/TableRow.vue'
import TableHead from '@/components/ui/table/TableHead.vue'
import TableFallback from '@/components/ui/table/TableFallback.vue'
import TableCell from '@/components/ui/table/TableCell.vue'
import Pagination from '@/components/ui/pagination/Pagination.vue'
import TableContainer from '@/components/ui/table/TableContainer.vue'
import { CreateButtonDTO, GetAllButtonsParams, useButton } from '@/composables/useButton'
import { Pagination as IPagination } from '@/interfaces/Pagination'
import { Button as IButton } from '@/interfaces/Button'
import TableRowSkeleton from '@/components/ui/table/TableRowSkeleton.vue'
import { useToast } from '@/components/ui/toast'
import { ButtonStatusEnum, ButtonStatusLabels } from '@/enums/buttons/ButtonStatusEnum'
import { Loader2 } from 'lucide-vue-next'
import DeleteConfirmationDialog from '@/components/ui/delete-dialog/DeleteConfirmationDialog.vue'
import StatusBadge from '@/components/ui/badge/StatusBadge.vue'
import PageTitle from '@/components/ui/page/PageTitle.vue'
import PageDescription from '@/components/ui/page/PageDescription.vue'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ButtonPlacementEnum, ButtonPlacementLabels } from '@/enums/buttons/ButtonPlacementEnum'

// Table Configuration
const tableHeaders = [
  { key: 'name', label: 'Nome', sortable: false },
  { key: 'url', label: 'Link', sortable: false },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: '', class: 'w-32' },
]

// Sample data (empty for demo)
const buttons = ref<IPagination<IButton>>()
const { getAll, create, update, destroy } = useButton()
const { toast } = useToast()

// Dialog and Form
const isSaving = ref(false)
const isButtonDialogOpen = ref(false)
const editingButton = ref<number | null>(null)
const deletingButton = ref<number | null>(null)
const isDeleteButtonDialogOpen = ref(false)
const isDeletingButton = ref(false)
const buttonForm = ref<Partial<CreateButtonDTO>>({})
const perPage = ref('10')

// Utils
const copyLink = async (url: string) => {
  await navigator.clipboard.writeText(url)
  // Add toast notification here
}

const openLink = (url: string) => {
  window.open(url, '_blank')
}

// Actions
const openNewLinkDialog = () => {
  editingButton.value = null
  buttonForm.value = { status: ButtonStatusEnum.ACTIVE, style: undefined }
  isButtonDialogOpen.value = true
}

const closeButtonDialog = () => {
  isButtonDialogOpen.value = false
}

const editButton = (button: IButton) => {
  editingButton.value = button.id
  buttonForm.value = {
    name: button.name,
    url: button.url,
    status: button.status,
    placement: button.placement,
    style: button.style
  }
  isButtonDialogOpen.value = true
}

const saveButton = () => {
  isSaving.value = true

  if (!editingButton.value) {
    create(buttonForm.value as CreateButtonDTO).then(() => {
      isSaving.value = false
      toast({
        title: 'Link criado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      getAll().then((data) => buttons.value = data)
      closeButtonDialog()
    }).catch(() => {
      isSaving.value = false
    })
  } else {
    update(editingButton.value, buttonForm.value).then(() => {
      isSaving.value = false
      toast({
        title: 'Link editado com sucesso',
        class: 'bg-green-500 text-white',
        duration: 1500
      })
      const index = buttons.value?.data.findIndex(button => button.id == editingButton.value)
      if (index !== -1 && index !== null && index !== undefined) {
        buttons.value!.data[index] = {
          ...buttons.value!.data[index],
          ...buttonForm.value
        }
      }
      closeButtonDialog()
    }).catch(() => {
      isSaving.value = false
    })
  }
}

const deleteLink = (button: IButton) => {
  isDeleteButtonDialogOpen.value = true
  deletingButton.value = button.id
}

const sortBy = (key: string) => {
  // Implement sorting logic
}

const reloadAndGetAll = (params: GetAllButtonsParams) => {
  buttons.value = undefined
  getAll(params).then((data) => buttons.value = data)
}

watch(() => perPage.value, () => {
  reloadAndGetAll({ perPage: perPage.value })
})

onMounted(() => {
  getAll().then((data) => buttons.value = data)
})
</script>